import { LightningElement, api, track, wire } from "lwc";
import { c as classNames } from "tds/chunk_index";
import { LabelsAdapter } from "common/labelsAdapter";
import { attrToBool } from "common/helpers";
import input$1 from "./input.html";
import inputSearch from "./inputSearch.html";
import inputCheckbox from "./inputCheckbox.html";
import inputRadio from "./inputRadio.html";
import inputToggle from "./inputToggle.html";
import inputFile from "./inputFile.html";

var data$9 = {
  error: "Geben Sie für die Suche mindestens drei Zeichen ein",
  loading: "Wird geladen...",
  deleteLabel: "Löschen",
};
data$9.error;
data$9.loading;
data$9.deleteLabel;

var data$8 = {
  error: "Enter at least 3 characters to search",
  loading: "Loading...",
  deleteLabel: "Delete",
};
data$8.error;
data$8.loading;
data$8.deleteLabel;

var data$7 = {
  error: "Introduzca como mínimo 3 caracteres para la búsqueda",
  loading: "Cargando...",
  deleteLabel: "Eliminar",
};
data$7.error;
data$7.loading;
data$7.deleteLabel;

var data$6 = { error: "Enter at least 3 characters to search" };
data$6.error;

var data$5 = {
  error: "Saisissez au moins 3 caractères à rechercher",
  loading: "Chargement...",
  deleteLabel: "Supprimer",
};
data$5.error;
data$5.loading;
data$5.deleteLabel;

var data$4 = {
  error: "Inserisci almeno 3 caratteri per la ricerca",
  loading: "Caricamento in corso...",
  deleteLabel: "Elimina",
};
data$4.error;
data$4.loading;
data$4.deleteLabel;

var data$3 = {
  error: "検索するには 3 文字以上を入力してください",
  loading: "読み込んでいます...",
  deleteLabel: "削除",
};
data$3.error;
data$3.loading;
data$3.deleteLabel;

var data$2 = {
  error: "검색하려면 3자 이상 입력하세요",
  loading: "로드 중...",
  deleteLabel: "삭제",
};
data$2.error;
data$2.loading;
data$2.deleteLabel;

var data$1 = { error: "Enter at least 3 characters to search" };
data$1.error;

var data = { error: "Enter at least 3 characters to search" };
data.error;

var locales = /*#__PURE__*/ Object.freeze({
  __proto__: null,
  de: data$9,
  en: data$8,
  es: data$7,
  esMX: data$6,
  fr: data$5,
  it: data$4,
  ja: data$3,
  ko: data$2,
  ptBR: data$1,
  zhCN: data,
});

function isToggleable(type) {
  return !!(type && ["checkbox", "radio", "toggle"].includes(type));
}
function isSearch(type) {
  return type === "search";
}
class input extends LightningElement {
  @api accept = null;
  @api ariaInvalid = null;
  @api ariaLabel = null;
  @api autocomplete = "off";
  @api busy = false;
  @api checked = false;
  @api disabled = false;
  @api errorMessage = null;
  @api label = null;
  @api fileSelectorLabel = null;
  @api fileSelectedLabel = null;
  @api name = null;
  @api placeholder = null;
  @api required = null;
  @api type = "text";
  @api value = "";
  @api readOnly = false;
  @api minSearchChars = null;
  @api subLabel = null;
  @api dragDropLabel = null;
  @api maxLength = null;
  @api fileLoading = false;
  @api fileImgUrl = "";
  @track file = null;
  loadingPreview = false;
  @wire(LabelsAdapter, {
    locales,
  })
  labels = null;
  @api toggleLabels = {
    toggleEnabled: "",
    toggleDisabled: "",
  };

  @api focus() {
    this.template.querySelector("input")?.focus();
  }

  render() {
    switch (this.type) {
      case "search":
        return inputSearch;

      case "checkbox":
        return inputCheckbox;

      case "radio":
        return inputRadio;

      case "toggle":
        return inputToggle;

      case "file":
        return inputFile;

      default:
        return input$1;
    }
  }

  get _value() {
    return typeof this.value === "string" ? this.value : null;
  }

  get className() {
    return classNames("input", {
      "input--disabled": this.isDisabled,
      "input--errored": this.hasError,
    });
  }

  get labelClasses() {
    return classNames("label", {
      "label--required": this.isRequired,
      "input-file__field-label": this.type === "file",
    });
  }

  get showClearButton() {
    return this._value && !this.isBusy;
  }

  get isDisabled() {
    return attrToBool(this.disabled);
  }

  get isRequired() {
    return attrToBool(this.required);
  }

  get isInvalid() {
    if (this.ariaInvalid || attrToBool(this.ariaInvalid)) {
      return attrToBool(this.ariaInvalid);
    }

    if ((this.isRequired && !this._value) || this.hasError) {
      return true;
    }

    if (this.minSearchChars && this.value.length < this.minSearchChars) {
      return true;
    }

    return false;
  }

  get isTypeText() {
    return this.type === "text";
  }

  get isBusy() {
    return attrToBool(this.busy);
  }

  get isChecked() {
    return attrToBool(this.checked);
  }

  get loading() {
    return this.loadingPreview || this.fileLoading;
  } // `file` is set when the user explicitly chooses a file.
  // `fileImgUrl` is set after the user chooses a file OR set when mounted in an edit form

  get hasFile() {
    return this.file || this.fileImgUrl;
  }

  handleChange(e) {
    if (isToggleable(this.type)) {
      return this.dispatchEvent(
        new CustomEvent("change", {
          detail: e.target.checked,
        })
      );
    }

    if (isSearch(this.type)) {
      this.value = e.target.value;

      if (this.minSearchChars && e.target.value.length >= this.minSearchChars) {
        this.errorMessage = "";
      }
    }

    if (this.type === "file") {
      const file = e.target.files[0];
      e.target.value = ""; // Allows same file selection after delete

      this.digestFile(file);
    } else if (!isSearch(this.type) || !this.minSearchChars) {
      this.dispatchEvent(
        new CustomEvent("change", {
          detail: e.target.value,
        })
      );
    }
  }

  handleClearValue(e) {
    e.preventDefault();
    this.value = "";
    this.errorMessage = "";
    this.dispatchEvent(
      new CustomEvent("change", {
        detail: "",
      })
    );
  }

  get hasError() {
    if (this.minSearchChars && this.value.length >= this.minSearchChars) {
      return false;
    }

    return !!this.errorMessage;
  }

  get minSearchCharError() {
    return this.labels.error;
  }

  handleKeyUp(e) {
    const input = e.target;

    if (this.ariaInvalid || attrToBool(this.ariaInvalid)) {
      return attrToBool(this.ariaInvalid);
    }

    if (this.isRequired) {
      input.value
        ? input.setAttribute("aria-invalid", "false")
        : input.setAttribute("aria-invalid", "true");
    }

    if (this.minSearchChars) {
      input.value.length >= this.minSearchChars
        ? input.setAttribute("aria-invalid", "false")
        : input.setAttribute("aria-invalid", "true");
    }
  }

  handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (isSearch(this.type) && this.minSearchChars) {
        this.value.length >= this.minSearchChars
          ? this.dispatchEvent(
              new CustomEvent("change", {
                detail: this.value,
              })
            )
          : (this.errorMessage = this.minSearchCharError);
      } else {
        this.dispatchEvent(
          new CustomEvent("change", {
            detail: this.value,
          })
        );
      }
    }
  };

  get isReadOnly() {
    return attrToBool(this.readOnly);
  }

  handleDragOver(e) {
    e.preventDefault();
  }

  handleDrop(e) {
    e.preventDefault();

    if (this.type === "file") {
      const file = e.dataTransfer.files[0];
      this.digestFile(file);
    }
  }

  digestFile(file) {
    if (file) {
      this.file = file;

      if (file.type.includes("image")) {
        this.displayFilePreview(file);
      }

      this.dispatchEvent(
        new CustomEvent("change", {
          detail: file,
        })
      );
    }
  }

  displayFilePreview(file) {
    const reader = new FileReader();

    reader.onload = () => {
      this.fileImgUrl = reader.result;
      this.loadingPreview = false;
    };

    this.loadingPreview = true;
    reader.readAsDataURL(file);
  }

  handleFileSelectorClick(e) {
    e.preventDefault();
    this.template.querySelector("input")?.click();
  }

  deleteFile(e) {
    e.preventDefault();
    this.file = null;
    this.fileImgUrl = null;
    this.dispatchEvent(
      new CustomEvent("change", {
        detail: null,
      })
    );
  }
}

export default input;
export { isSearch, isToggleable };
